import classNames from 'classnames';

import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToolbarInternalButtons,
  useMaterialReactTable,
} from 'material-react-table';
import { IconButton, Menu } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { mkConfig } from 'export-to-csv';
import { toast } from 'react-toastify';
import { PrintOutlined } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import { MenuProps } from '@mui/material/Menu';
import { createTheme, ThemeProvider, useTheme } from '@mui/material';
import { useMemo } from 'react';
import React from 'react';
import { ButtonIcon } from '../../atoms/buttons/ButtonIcon/ButtonIcon';
import { ArrowDownIcon } from '@heroicons/react/24/solid';
import {
  exportAllData,
  exportFilteredRows,
  exportRows,
} from '../../utils/tableExportUtils';

export interface TableProps {
  useCallbackClickRow?: any;
  inner?: boolean;
  name: string;
  columns: any;
  data: Record<string, any>[];
  onSelectRow?: any;
  initialState?: object;
  enableBottomToolbar?: boolean;
  enableTopToolbar?: boolean;
  enableColumnActions?: boolean;
  enableColumnFilters?: boolean;
  enableSorting?: boolean;
  enableExpanding?: boolean;
  enableExpandAll?: boolean;
  columnFilterDisplayMode: string;
  enableFacetedValues?: boolean;
  enableRowSelection?: boolean;
  enableToolbarInternalActions?: boolean;
  positionToolbarAlertBanner?: string;
  advancedTable?: boolean;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  manualSearch?: boolean;
  onPaginationChange?: (value: any) => void;
  manualPagination?: boolean;
  manualSorting?: boolean;
  // sorting?: any[];
  onSortChange?: (value: string) => void;
  /**
   * Optional async fetcher for exporting all data.
   * If provided, Table will call this to retrieve the full dataset when the user selects
   * "Export CSV (all rows)" instead of using the current props.data.
   */
  onRequestAllData?: (
    onProgress?: (loaded: number, total: number) => void
  ) => Promise<Record<string, any>[]>;
}

export function Table(props: any) {
  const {
    useCallbackClickRow,
    enableBottomToolbar = true,
    enableTopToolbar = true,
    enableColumnFilters = false,
    enableColumnActions = false,
    enableSorting = true,
    inner = false,
    onSelectRow,
    enableExpanding,
    enableExpandAll,
    enableFacetedValues = false,
    enableToolbarInternalActions = false,
    positionToolbarAlertBanner = 'bottom',
    columnFilterDisplayMode = 'custom',
    name = 'material-table',
    advancedTable = false,
    globalFilter,
    onGlobalFilterChange,
    manualSearch = false,
    manualPagination = false,
    onPaginationChange,
    manualSorting = false,
    onSortChange,
    ...restProps
  } = props;
  const currentPage = localStorage.getItem(`table-${name}-page-index`) || 0;
  const currentPageSize = localStorage.getItem(`table-${name}-page-size`);
  const [firstLaunch, setFirstLaunch] = React.useState(true);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = () => {
    setAnchorEl(buttonRef.current);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const table = useMaterialReactTable({
    data: props.data,
    columns: props.columns,
    initialState: props.initialState,
    columnFilterDisplayMode: columnFilterDisplayMode,
    enableBottomToolbar: enableBottomToolbar,
    positionToolbarAlertBanner: positionToolbarAlertBanner,
    enableColumnActions: enableColumnActions,
    enableColumnFilters: enableColumnFilters,
    enableSorting: enableSorting,
    enablePinning: true,
    enableTopToolbar: enableTopToolbar,
    enableExpanding: enableExpanding,
    enableExpandAll: enableExpandAll,
    manualPagination: manualPagination,
    manualSorting: manualSorting,
    ...(manualSorting ? { onSortingChange: onSortChange } : {}),
    ...(manualSearch ? { globalFilter, onGlobalFilterChange } : {}),
    ...(manualPagination ? { onPaginationChange } : {}),
    muiPaginationProps(props) {
      const page = props.table.getState().pagination.pageIndex;
      const pageSize = props.table.getState().pagination.pageSize;
      if (!firstLaunch) {
        localStorage.setItem(`table-${name}-page-index`, String(page));
        localStorage.setItem(`table-${name}-page-size`, String(pageSize));
      }
      if (firstLaunch) {
        const savedPageSize = Number(currentPageSize);
        if (!Number.isNaN(savedPageSize) && savedPageSize > 0) {
          props.table.setPageSize(() => savedPageSize);
        }
        props.table.setPageIndex(() => Number(currentPage));
      }
      setFirstLaunch(false);
    },
    defaultColumn: {
      minSize: 5,
      maxSize: 9001, //allow columns to get larger than default
      //size: 260, //make columns wider by default
    },
    enableToolbarInternalActions: enableToolbarInternalActions,
    positionGlobalFilter: 'left',
    enableFacetedValues: enableFacetedValues,
    muiBottomToolbarProps: {
      className: classNames({ '!hidden': props.enablePagination === false }),
    },
    muiTopToolbarProps: ({ table }) => ({
      className:
        '!bg-gray-100 !border-gray-200 border-b-[1px] !min-h-[82px] !p-5',
    }),
    muiTablePaperProps: ({ table }) => ({
      style: {
        zIndex: table.getState().isFullScreen ? 10000 : undefined,
        position: table.getState().isFullScreen ? 'fixed' : undefined,
        top: table.getState().isFullScreen ? '143px' : undefined,
        left: table.getState().isFullScreen ? '0' : undefined,
        right: table.getState().isFullScreen ? '0' : undefined,
        bottom: table.getState().isFullScreen ? '0' : undefined,
        width: table.getState().isFullScreen ? '100%' : undefined,
        height: table.getState().isFullScreen
          ? 'calc(100vh - 143px)'
          : undefined,
        overflow: table.getState().isFullScreen ? 'auto' : undefined,
      },
      elevation: 0,
      className: classNames(
        '!rounded-lg !border-[1px] !border-gray-200 !overflow-hidden',
        { '!rounded-none !border-none': inner }
      ),
    }),
    muiTableHeadCellProps: {
      className:
        '!font-nunito !bg-gray-50 !text-sm !uppercase !text-gray-700 !py-2 !pl-2 !pr-0 !align-middle',
      sx: {
        '.Mui-TableHeadCell-Content-Wrapper': { minWidth: '0px' },
      },
    },
    muiTableBodyRowProps: (data: any) => ({
      onClick: (event: any) => {
        //preventing row click if there other custom component inside cell
        if (
          String(event?.target?.className)?.includes('MuiTableCell-root') &&
          (useCallbackClickRow || onSelectRow)
        ) {
          useCallbackClickRow && useCallbackClickRow(data.row?.original);
          onSelectRow && onSelectRow(data.row?.index);
        }
      },
      className: classNames(
        'relative z-0 hover:z-10 !font-nunito !text-base !text-gray-900 !overflow-visible !pl-2 !pr-0 hover:!outline-primary-900',
        { '!cursor-pointer': useCallbackClickRow || onSelectRow }
      ),
    }),
    muiTableBodyCellProps: (data: any) => ({
      onClick: (event: any) => {
        //preventing row click if there other custom component inside cell
        if (
          String(event?.target?.className)?.includes('MuiTableCell-root') &&
          (useCallbackClickRow || onSelectRow)
        ) {
          useCallbackClickRow && useCallbackClickRow(data.row?.original);
          onSelectRow && onSelectRow(data.row?.index);
        }
      },
      className: classNames(
        '!font-nunito !text-base !text-gray-900 !overflow-visible !pl-2 !pr-0 hover:!outline-primary-900',
        { '!cursor-pointer': useCallbackClickRow || onSelectRow }
      ),
    }),
    ...restProps,
    muiSearchTextFieldProps: {
      placeholder: 'Search',
      variant: 'outlined',
      margin: 'none',
      ...(!advancedTable && {
        className: '!my-3 bg-white md:!w-[400px]',
        sx: {
          '> .MuiOutlinedInput-root': {
            '& > fieldset': { borderRadius: '10px', backgroundColor: '#fff' },
            '& > input': { padding: '12px 0', backgroundColor: '#fff' },
            '& > svg': { width: '18px', height: '18px' },
          },
        },
      }),
      ...(advancedTable && {
        className: 'bg-white md:!w-[400px]',
        sx: {
          '> .MuiOutlinedInput-root': {
            '& > fieldset': { borderRadius: '10px' },
            '& > input': {
              borderRadius: '10px',
              padding: '0.75rem 0', // 12px 0
              backgroundColor: '#fff',
            },
            '& > svg': { width: '18px', height: '18px' },
          },
        },
      }),
      ...(props.muiSearchTextFieldProps || {}),
    },

    muiFilterTextFieldProps: ({ column }) => ({
      variant: 'outlined',
      margin: 'none',
      className: 'bg-white font-nunito',
      sx: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderRadius: '5px', padding: '0' },
          '& input': {
            width: '100%',
            borderRadius: '5px',
            padding: '8px 5px',
            fontSize: '12px',
          },
          '& .MuiSelect-select': {
            borderRadius: '5px',
            padding: '8px 5px',
            fontSize: '12px',
          },
        },
        '& .MuiPaper-root': {
          padding: 0,
          borderRadius: 6,
          minWidth: 180,
          fontSize: 12,
          color: 'rgb(55, 65, 81)',
          '& .MuiMenu-list': {
            padding: '0',
            fontSize: 12,
          },
          '& .MuiMenuItem-root': {
            fontSize: 12,
            padding: 0,
            '& .MuiSvgIcon-root': {
              fontSize: 12,
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
    }),
  });

  const globalTheme = useTheme(); //(optional) if you already have a theme defined in your app root, you can import here

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: 'rgb(59 125 112)',
          },
        },
        typography: {
          fontFamily: [
            'Nunito',
            'ui-sans-serif',
            'system-ui',
            '-apple-system',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
        },
      }),
    [globalTheme]
  );

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: `data-export-${new Date().toISOString()}`,
  });

  const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      fontSize: 12,
      marginTop: theme.spacing(1),
      minWidth: 180,
      fontFamily: 'Nunito',
      color: 'rgb(55, 65, 81)',
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        fontSize: 14,
        '& .MuiSvgIcon-root': {
          fontSize: 12,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {},
      },
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[300],
      }),
    },
  }));

  // progress label/percent helpers for export button
  const isDeterminate = Boolean(
    exporting && exportProgress && (exportProgress.total ?? 0) > 0
  );
  const percent = isDeterminate
    ? Math.min(
        100,
        Math.floor(
          (exportProgress!.loaded / Math.max(1, exportProgress!.total)) * 100
        )
      )
    : undefined;
  const exportLabel =
    exporting && exportProgress
      ? isDeterminate
        ? `Exporting ${percent}%`
        : `Exporting ${exportProgress.loaded}%`
      : 'Export data';

  // @ts-ignore
  return (
    <ThemeProvider theme={tableTheme}>
      {advancedTable && (
        <div
          style={{
            position: table.getState().isFullScreen ? 'fixed' : undefined,
            top: table.getState().isFullScreen ? '74px' : undefined,
            left: table.getState().isFullScreen ? 0 : undefined,
            right: table.getState().isFullScreen ? 0 : undefined,
            width: table.getState().isFullScreen ? '100%' : undefined,
          }}
        >
          {/* top filter container */}
          <div className="flex flex-col justify-between gap-3 rounded bg-white p-3 lg:flex-row">
            {/* export button */}
            <div className="hidden flex-row items-center gap-2 py-2 md:flex">
              <div ref={buttonRef}>
                <ButtonIcon
                  label={exportLabel}
                  iconLeft={true}
                  Icon={ArrowDownIcon}
                  onClick={handleClick}
                  kind={'bgLightGreen'}
                  className="font-nunito relative overflow-hidden text-sm"
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  disabled={exporting}
                  progressPercent={percent}
                />
              </div>

              <StyledMenu
                anchorEl={anchorEl}
                open={open}
                className="font-nunito text-sm"
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem
                  className="font-nunito"
                  disabled={exporting}
                  onClick={async () => {
                    try {
                      if (props.onRequestAllData) {
                        setExporting(true);
                        try {
                          window.dispatchEvent(
                            new CustomEvent('registrations-export-start')
                          );
                        } catch {}
                        setExportProgress({ loaded: 0, total: 0 });
                        const allData = await props.onRequestAllData(
                          (loaded?: number, total?: number) => {
                            if (
                              typeof loaded === 'number' &&
                              typeof total === 'number'
                            ) {
                              setExportProgress({ loaded, total });
                            }
                          }
                        );
                        exportAllData(allData, table, csvConfig, handleClose);
                        toast.success(
                          'Data export has been completed and downloaded successfully.'
                        );
                      } else {
                        exportAllData(
                          props.data,
                          table,
                          csvConfig,
                          handleClose
                        );
                        toast.success(
                          'Data export has been completed and downloaded successfully.'
                        );
                      }
                    } catch (e) {
                      console.error('Error exporting all data:', e);
                    } finally {
                      setExporting(false);
                      setExportProgress(null);
                      try {
                        window.dispatchEvent(
                          new CustomEvent('registrations-export-end')
                        );
                      } catch {}
                      handleClose();
                    }
                  }}
                >
                  Export CSV (all rows)
                </MenuItem>
                <MenuItem
                  className="font-nunito"
                  disabled={
                    table.getFilteredRowModel().rows.length ===
                    props.data.length
                  }
                  onClick={() =>
                    exportFilteredRows(table, csvConfig, handleClose)
                  }
                >
                  Export CSV (filtered rows)
                </MenuItem>
                <MenuItem
                  disabled={
                    !table.getIsSomeRowsSelected() &&
                    !table.getIsAllRowsSelected()
                  }
                  onClick={() =>
                    exportRows(
                      table.getSelectedRowModel().rows,
                      table,
                      csvConfig,
                      handleClose
                    )
                  }
                >
                  Export CSV (selected rows)
                </MenuItem>
              </StyledMenu>
            </div>

            {/* search-wrapper */}
            <div className="flex flex-col items-center gap-4 md:flex-row">
              <MRT_GlobalFilterTextField
                sx={{ marginRight: 0 }}
                table={table}
              />

              {/* search right icons */}
              <div className="ml-auto hidden flex-shrink-0 items-center gap-1 md:flex">
                <IconButton
                  onClick={() => window.print()}
                  aria-label="Print table"
                >
                  <PrintOutlined />
                </IconButton>
                <MRT_ToolbarInternalButtons className="gap-1" table={table} />
              </div>
            </div>
          </div>
        </div>
      )}

      <MaterialReactTable table={table} />
    </ThemeProvider>
  );
}

export default Table;
